import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { publicRecruitmentApi } from '../../api/publicRecruitment';
import { CareersNav } from './CareersNav';

const EMPLOYMENT_TYPE_LABELS: Record<string, string> = { full_time: 'Full-Time', part_time: 'Part-Time', consultant: 'Consultant', intern: 'Intern', volunteer: 'Volunteer' };

function daysAgo(dateStr: string | null): string {
  if (!dateStr) return '';
  const days = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
  if (days <= 0) return 'Posted today';
  if (days === 1) return 'Posted yesterday';
  return `Posted ${days} days ago`;
}

/** Genuinely public - no authentication anywhere on this page or anything it calls. */
export function CareersListPage() {
  const vacanciesQuery = useQuery({ queryKey: ['public-vacancies'], queryFn: () => publicRecruitmentApi.listVacancies() });
  const [search, setSearch] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  const vacancies = vacanciesQuery.data?.data ?? [];

  // Real filter options built from the actual data returned - never
  // a fabricated category list unconnected to what's genuinely open.
  const locations = useMemo(() => Array.from(new Set(vacancies.map((v) => v.duty_station_name).filter(Boolean))) as string[], [vacancies]);
  const types = useMemo(() => Array.from(new Set(vacancies.map((v) => v.employment_type).filter(Boolean))) as string[], [vacancies]);

  const filtered = vacancies.filter((v) => {
    const matchesSearch = !search || (v.position_title ?? '').toLowerCase().includes(search.toLowerCase()) || (v.department_name ?? '').toLowerCase().includes(search.toLowerCase());
    const matchesLocation = !locationFilter || v.duty_station_name === locationFilter;
    const matchesType = !typeFilter || v.employment_type === typeFilter;
    return matchesSearch && matchesLocation && matchesType;
  });

  return (
    <div className="careers-page careers-page--wide">
      <CareersNav />

      <div className="careers-photo-hero">
        <img src="/verdantrise-team.jpg" alt="Verdantrise Humanitarian Initiative team" />
        <div className="careers-photo-hero-caption">
          <span>Careers that <strong>change lives</strong></span>
        </div>
      </div>

      <p className="careers-tagline-inline">Compassion · Integrity · Empowerment · Accountability</p>

      <div className="careers-search-bar">
        <input type="search" placeholder="Search for jobs or keywords" value={search} onChange={(e) => setSearch(e.target.value)} />
        <select value={locationFilter} onChange={(e) => setLocationFilter(e.target.value)}>
          <option value="">Location</option>
          {locations.map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
          <option value="">Contract Type</option>
          {types.map((t) => <option key={t} value={t}>{EMPLOYMENT_TYPE_LABELS[t] ?? t}</option>)}
        </select>
      </div>

      {vacanciesQuery.isLoading && <p className="muted">Loading open positions…</p>}
      {vacanciesQuery.data && (
        <p className="careers-results-count">{filtered.length} {filtered.length === 1 ? 'JOB' : 'JOBS'} FOUND</p>
      )}
      {vacanciesQuery.data && filtered.length === 0 && <p className="muted">No open positions match your search. Please check back soon.</p>}

      <div className="careers-vacancy-list careers-vacancy-list--wfp">
        {filtered.map((v) => (
          <Link key={v.id} to={`/careers/vacancies/${v.id}`} className="careers-vacancy-row">
            <h3>{v.position_title ?? 'Position'}</h3>
            <p className="careers-vacancy-meta">
              <span>&#128205; {v.duty_station_name ?? '-'}</span>
              <span>&#128337; {daysAgo(v.opening_date)}</span>
              {v.closing_date && <span>&#9203; Closes {new Date(v.closing_date).toLocaleDateString()}</span>}
            </p>
            <p className="careers-vacancy-ref">{v.vacancy_reference}</p>
          </Link>
        ))}
      </div>
    </div>
  );
}
